import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IAllStates } from '../../../../../../../store/rootReducer';
import { User } from '../../../../../../../model/user/User';
import usePagination from '../../../../../../../shared/hooks/usePagination';
import { CustomAutocomplete } from '../../../../../../../shared/components/Autocomplete';
import { ReplaceInfluencerIcon } from '../../../../../../../shared/Icons';
import { CustomImageHandler } from '../../../../../../../shared/components/CustomImageHandler';
import {
    clearHtmlFromString,
    getFirstPreviewImage,
    truncateString,
} from '../../../../../../../shared/functions/Functions';
import { Card } from 'react-bootstrap';
import { setActiveTab, setInitialState, setSelectedUser } from '../../../../../../../store/brand/childOfferBrandSlice';
import { ErrorToast } from '../../../../../../../utils/toasters';
import { BookingService } from '../../../service';
import { InternalRoutes } from '../../../../../../../enum/InternalRoutes';
import { useNavigate } from 'react-router-dom';
import TooltipOverlay from '../../../../../../../shared/components/tooltip/Tooltip';
import { InfluencerSelectAutocomplete } from '../../../../../../../shared/components/InfluencerSelectAutocomplete';

interface IChildOfferModalLeftSideProps {
    campaignId: number;
}

export function ChildOfferModalLeftSide({ campaignId }: IChildOfferModalLeftSideProps) {
    const { campaign, selectedUser } = useSelector((state: IAllStates) => state.childOfferBrand);
    const { isClient } = useSelector((state: IAllStates) => state.auth.user);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [matchedUsers, setMatchedUsers] = useState<User[]>([]);
    const { filterParams, setFilterParams } = usePagination({
        otherKeys: {
            page: 1,
            perPage: 0,
        },
    });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isLongTerm = campaign?.offers[0]?.isLongTerm;

    useEffect(() => {
        BookingService.getContractUsers(String(campaignId), filterParams)
            .then(response => {
                setMatchedUsers(response.data);
            })
            .catch(err => ErrorToast(err));
    }, [filterParams]);

    function handleUsers(paramKey: string, paramValue: string | string[] | number) {
        const selectedUserCopy = matchedUsers.find(item => item.id === +paramValue);
        dispatch(setInitialState());
        dispatch(setSelectedUser(selectedUserCopy));
        setIsSearchOpen(false);
        setFilterParams((prev: any) => ({ ...prev, displayName: '' }));
    }

    const goToContentTab = () => {
        dispatch(setActiveTab('1'));
    };

    const goToProfileInfo = () => {
        if (isClient) {
            return;
        }
        navigate(`${InternalRoutes.INFLUENCER_PUBLIC_PROFILE}/${selectedUser?.id}`);
    };
    const displayName = selectedUser?.displayName || '';
    const truncatedDisplayName = truncateString(displayName, 25);
    const isTruncatedDisplayName = displayName.length > 25;
    const name = `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim();
    const truncatedName = truncateString(name, 25);
    const isTruncated = name.length > 25;
    return (
        <>
            <Card className="d-none d-xl-block">
                <Card.Body className="d-flex flex-column p-0">
                    <div className="text-center">
                        {!isClient && (
                            <div className="p-3">
                                <InfluencerSelectAutocomplete
                                    dataArray={matchedUsers}
                                    selected={selectedUser}
                                    onChange={user => handleUsers('assignedUserId', user?.id)}
                                />
                            </div>
                        )}
                        <div className={`d-flex align-items-center justify-content-center ${isClient ? 'mt-4' : ''}`}>
                            <CustomImageHandler
                                photoPath={selectedUser?.profilePhoto?.thumbnail ?? ''}
                                thumbnailPath={selectedUser?.profilePhoto?.thumbnail ?? ''}
                                altTag="User Profile"
                                classes="agency-offer-profile-img"
                                name={displayName}
                            />
                        </div>

                        <TooltipOverlay content={isTruncatedDisplayName ? displayName : ''}>
                            <h6 onClick={goToProfileInfo} className={`mt-3 mb-1 text-center w-100 ${!isClient && 'cursor-pointer'}`}>
                                {truncatedDisplayName || '-'}
                            </h6>
                        </TooltipOverlay>

                        <TooltipOverlay content={isTruncated ? name : ''}>
                            <p onClick={goToProfileInfo} className={`mb-3 text-muted text-center w-100 ${!isClient && 'cursor-pointer'}`}>
                                {truncatedName || '-'}
                            </p>
                        </TooltipOverlay>
                    </div>
                    <div className="p-3">
                        <div className="position-relative overflow-hidden">
                            {getFirstPreviewImage(
                                'style_uploaded_image',
                                campaign?.campaignImages,
                                'default-radius img-cover moonboard-images'
                            )}
                            {isLongTerm && (
                                <div className="diagonal-text">
                                    <span className="diagonal-text-sm">
                                        {t(`brand.campaign.briefing.overview_card.long_term`)}
                                    </span>
                                </div>
                            )}
                        </div>

                        {campaign?.offers[0]?.status && (
                            <p className={`place-top-right status ${campaign?.offers[0]?.status} d-md-none mr-3`}>
                                {campaign?.offers[0]?.status}
                            </p>
                        )}
                        <h5 className="my-3">
                            {campaign?.isCharity && (
                                <>
                                    <div className="d-inline-block">(</div>
                                    <div className="text-primary-dark d-inline-block">
                                        {t('general.button.barter_deal')}
                                    </div>
                                    <div className="d-inline-block">)</div>
                                </>
                            )}{' '}
                            {campaign?.title}
                        </h5>
                        <h6>{t('influencer.offer_management.modal.campaign_description')}</h6>
                        <div className="five-line-text">{clearHtmlFromString(campaign?.adDescription ?? '-')}</div>
                        <div className="d-flex justify-content-end">
                            <a className="text-muted cursor-pointer" onClick={goToContentTab}>
                                {t('general.read_more')}
                            </a>
                        </div>
                    </div>
                </Card.Body>
            </Card>
            <div className="d-xl-none d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom-primary">
                {isSearchOpen && !isClient ? (
                    <div className="d-flex align-items-center justify-content-center m-2 w-100">
                        <CustomAutocomplete
                            dataArray={matchedUsers}
                            isWithImage={true}
                            isMultiple={false}
                            inputLabel="firstName"
                            queryFilterKey="assignedUserId"
                            photoLabel="profilePhoto.path"
                            selectedStateImage={selectedUser?.profilePhoto?.path as string}
                            key={selectedUser?.id}
                            // defaultArray={matchedUsers?.find(user => Number(user.id) === +selectedMatchedUser?.id) as unknown as []}
                            inputLabelTranslation={t('agency.filter.matched_users')}
                            handleChangeParams={handleUsers}
                        />
                    </div>
                ) : (
                    <>
                        <div className="d-flex align-items-center">
                            <CustomImageHandler
                                photoPath={selectedUser?.profilePhoto?.path ?? ''}
                                altTag="User Profile"
                                classes="agency-offer-profile-img"
                                name={selectedUser?.firstName}
                            />
                            <h6 className="mb-0 ml-2">
                                {selectedUser?.firstName} {selectedUser?.lastName}
                            </h6>
                        </div>
                        {!isClient && (
                            <ReplaceInfluencerIcon
                                width="33"
                                height="22"
                                onClick={() => setIsSearchOpen(prevState => !prevState)}
                                classes="cursor-pointer"
                            />
                        )}
                    </>
                )}
            </div>
            <div className="d-xl-none d-flex align-items-center">
                <div>{getFirstPreviewImage('style_uploaded_image', campaign?.campaignImages, 'campaign-icon')}</div>
                <h5 className="mb-0 ml-2">
                    {campaign?.isCharity && (
                        <>
                            <div className="d-inline-block">(</div>
                            <div className="text-primary-dark d-inline-block">{t('general.button.barter_deal')}</div>
                            <div className="d-inline-block">)</div>
                        </>
                    )}{' '}
                    {campaign?.title}
                </h5>
            </div>
        </>
    );
}
